import type { GetMeasureTemplatesQuery } from '@/types/__generated__/graphql';
import { Dialog, IconButton, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import type { SyntheticEvent } from 'react';
import { useState } from 'react';
import { Download, Upload } from 'react-bootstrap-icons';
import { ExportPlanDialogContent } from './ExportPlanDialogContent';
import { ImportPlanDialogContent } from './ImportPlanDialogContent';
import { UploadLegacyDataButton } from '../UploadLegacyDataButton';
import { usePermissions } from '@/hooks/use-user-profile';
type Props = {
  measureTemplates: NonNullable<GetMeasureTemplatesQuery['framework']>;
};
interface TabPanelProps {
  children?: React.ReactNode;
  tabId: string;
  activeTab: string;
}
function TabPanel({
  children,
  tabId,
  activeTab,
  ...rest
}: TabPanelProps) {
  return <div role="tabpanel" hidden={tabId !== activeTab} id={`import-export-tabpanel-${tabId}`} aria-labelledby={`import-export-tab-${tabId}`} {...rest} data-sentry-component="TabPanel" data-sentry-source-file="ImportExportActions.tsx">
      {activeTab === tabId && children}
    </div>;
}
function tabA11yProps(tabId: string) {
  return {
    id: `import-export-tab-${tabId}`,
    'aria-controls': `import-export-tabpanel-${tabId}`
  };
}
export function ImportExportActions({
  measureTemplates
}: Props) {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'export' | 'import'>('export');
  const {
    isFrameworkAdmin
  } = usePermissions();
  function handleClose() {
    setModalOpen(false);
  }
  function handleChangeTab(e: SyntheticEvent, value: 'export' | 'import') {
    setActiveTab(value);
  }
  function handleClickExport() {
    setActiveTab('export');
    setModalOpen(true);
  }
  function handleClickImport() {
    setActiveTab('import');
    setModalOpen(true);
  }
  return <>
      <Stack spacing={2} direction="row" data-sentry-element="Stack" data-sentry-source-file="ImportExportActions.tsx">
        <IconButton color="primary" onClick={handleClickExport} data-sentry-element="IconButton" data-sentry-source-file="ImportExportActions.tsx">
          <Tooltip title="Export plan data" placement="top" data-sentry-element="Tooltip" data-sentry-source-file="ImportExportActions.tsx">
            <Download size={24} data-sentry-element="Download" data-sentry-source-file="ImportExportActions.tsx" />
          </Tooltip>
        </IconButton>
        <IconButton color="primary" onClick={handleClickImport} data-sentry-element="IconButton" data-sentry-source-file="ImportExportActions.tsx">
          <Tooltip title="Import plan data" placement="top" data-sentry-element="Tooltip" data-sentry-source-file="ImportExportActions.tsx">
            <Upload size={24} data-sentry-element="Upload" data-sentry-source-file="ImportExportActions.tsx" />
          </Tooltip>
        </IconButton>
        {isFrameworkAdmin && <UploadLegacyDataButton measureTemplates={measureTemplates} />}
      </Stack>

      <Dialog fullWidth maxWidth="md" onClose={handleClose} open={isModalOpen} data-sentry-element="Dialog" data-sentry-source-file="ImportExportActions.tsx">
        <Tabs value={activeTab} onChange={handleChangeTab} aria-label="import or export plan data tabs" sx={{
        bgcolor: theme => theme.palette.brand[50]
      }} data-sentry-element="Tabs" data-sentry-source-file="ImportExportActions.tsx">
          <Tab label="Export" value="export" {...tabA11yProps('export')} data-sentry-element="Tab" data-sentry-source-file="ImportExportActions.tsx" />
          <Tab label="Import" value="import" {...tabA11yProps('import')} data-sentry-element="Tab" data-sentry-source-file="ImportExportActions.tsx" />
        </Tabs>

        <TabPanel activeTab={activeTab} tabId="export" data-sentry-element="TabPanel" data-sentry-source-file="ImportExportActions.tsx">
          <ExportPlanDialogContent measureTemplates={measureTemplates} onClose={handleClose} data-sentry-element="ExportPlanDialogContent" data-sentry-source-file="ImportExportActions.tsx" />
        </TabPanel>
        <TabPanel activeTab={activeTab} tabId="import" data-sentry-element="TabPanel" data-sentry-source-file="ImportExportActions.tsx">
          <ImportPlanDialogContent onClose={handleClose} data-sentry-element="ImportPlanDialogContent" data-sentry-source-file="ImportExportActions.tsx" />
        </TabPanel>
      </Dialog>
    </>;
}